import { createContext, useContext, type ReactNode } from "react"

/**
 * Truthy inside AppShell's <Outlet>. Any <Sidebar> / <Header> rendered
 * by a page component checks this — if it's already inside AppShell's
 * own chrome, the redundant instance returns null instead of double-
 * rendering.
 *
 * Why: pages ported from Next.js App Router assumed a layout.tsx
 * ancestor provided sidebar + header. In our react-router monorepo
 * setup we don't have layout.tsx, so AppShell provides the chrome
 * uniformly for every authenticated route. But ~38 already-ported
 * pages (the earlier phases) also render their own Sidebar + Header
 * inside a wrapper div. Rather than mechanically strip those out of
 * every file (109+ pages potentially affected), the components short-
 * circuit via this context. Result:
 *
 *   AppShell:            renders Sidebar + Header + Outlet
 *   AppShell.Provider:   sets flag=true around Outlet
 *   Page inside Outlet:  <Sidebar/> -> null; <Header/> -> null
 *
 * The AppShell-provided Sidebar / Header are rendered OUTSIDE the
 * Provider so they still render normally.
 */
const AppShellChromeContext = createContext<boolean>(false)

export function useIsInsideAppShellChrome(): boolean {
  return useContext(AppShellChromeContext)
}

export function AppShellChromeProvider({ children }: { children: ReactNode }) {
  return (
    <AppShellChromeContext.Provider value={true}>{children}</AppShellChromeContext.Provider>
  )
}
